import { Button, SelectOptionType } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import Loading from '../../../../../components/info/Loading';

import {
  AxiosResult,
  AxiosResultDefaultError,
} from '../../../../../api/request';
import {
  Discount,
  DiscountFields,
  DiscountFormOptions,
  DiscountWithRelations,
  getDiscountById,
  getDiscountFormOptions,
  PeriodWithDates,
  saveDiscountRequest,
} from '../../api/discount';

import {
  TariffFormatter,
  checkCurrencyAsNumber,
} from '../../../../../utils/currency';
import DiscountDataSection from './sections/DiscountDataSection';
import DiscountInicioSection from './sections/DiscountInicioSection';
import DiscountContinuidadSection from './sections/DiscountContinuidadSection';

const prefix = 'tariffAndTuition.discounts';

// Agrupación de tipos de aplicación por el campo de valor que usan (HU 16116 v2).
const PERCENTAGE_TYPES = ['porcentaje_fijo', 'porcentaje_maximo'];
const AMOUNT_TYPES = ['monto_fijo', 'monto_maximo'];

interface DiscountFormProps {
  recordToEdit: Discount | null;
  onSubmit: (result: AxiosResult<Discount, AxiosResultDefaultError>) => void;
  onCancel: () => void;
}

interface FormValues
  extends Pick<DiscountFields, 'code' | 'name' | 'description'> {
  benefitType: SelectOptionType | null;
  applyTo: SelectOptionType | null;
  applicationType: SelectOptionType | null;
  applicationValue: string;
  canCombineWithOtherBenefits: boolean;
  requiresDocumentValidation: SelectOptionType | null;
  applicableByEnroller: boolean;
  capType: SelectOptionType | null;
  maxAmount: string;
  maxQuota: string;
  hasAutomaticAssignment: boolean;
  applyToAllNewAdmissionProcesses: boolean;
  minimumGradeAverage: string;
  inicioAcademicLevelIds: SelectOptionType[];
  inicioPeriodIds: SelectOptionType[];
  inicioStudyPlanIds: SelectOptionType[];
  inicioCampusIds: SelectOptionType[];
  inicioScheduleIds: SelectOptionType[];
  inicioModalityIds: SelectOptionType[];
  continuidadAcademicLevelIds: SelectOptionType[];
  continuidadStudyPlanIds: SelectOptionType[];
  continuidadCampusIds: SelectOptionType[];
  continuidadScheduleIds: SelectOptionType[];
  continuidadModalityIds: SelectOptionType[];
  continuidadPeriodIds: SelectOptionType[];
  enrollmentDates: { periodId: number; startDate: string; endDate: string }[];
  earlyPaymentScope: SelectOptionType | null;
  earlyPaymentCashFrom: string;
  earlyPaymentCashTo: string;
  earlyPaymentInstalmentsFrom: string;
  earlyPaymentInstalmentsTo: string;
}

const DEFAULT_VALUES: FormValues = {
  code: '',
  name: '',
  description: '',
  benefitType: null,
  applyTo: null,
  applicationType: null,
  applicationValue: '',
  canCombineWithOtherBenefits: false,
  requiresDocumentValidation: null,
  applicableByEnroller: false,
  capType: null,
  maxAmount: '',
  maxQuota: '',
  hasAutomaticAssignment: false,
  applyToAllNewAdmissionProcesses: false,
  minimumGradeAverage: '',
  inicioAcademicLevelIds: [],
  inicioPeriodIds: [],
  inicioStudyPlanIds: [],
  inicioCampusIds: [],
  inicioScheduleIds: [],
  inicioModalityIds: [],
  continuidadAcademicLevelIds: [],
  continuidadStudyPlanIds: [],
  continuidadCampusIds: [],
  continuidadScheduleIds: [],
  continuidadModalityIds: [],
  continuidadPeriodIds: [],
  enrollmentDates: [],
  earlyPaymentScope: null,
  earlyPaymentCashFrom: '',
  earlyPaymentCashTo: '',
  earlyPaymentInstalmentsFrom: '',
  earlyPaymentInstalmentsTo: '',
};

export const DiscountForm = ({
  recordToEdit,
  onSubmit: onSubmitProp,
  onCancel,
}: DiscountFormProps) => {
  const { t } = useTranslation();
  const editing = !!recordToEdit;

  // Mapas value->label para reconstruir los selects de enums fijos en edición.
  const applyToLabels: Record<string, string> = {
    arancel: t(`${prefix}.benefit.applyToArancel`),
    matricula: t(`${prefix}.benefit.applyToMatricula`),
    ambos: t(`${prefix}.benefit.applyToBoth`),
  };
  const applicationTypeLabels: Record<string, string> = {
    porcentaje_fijo: t(`${prefix}.benefit.percentageFixed`),
    porcentaje_maximo: t(`${prefix}.benefit.percentageMax`),
    monto_fijo: t(`${prefix}.benefit.amountFixed`),
    monto_variable: t(`${prefix}.benefit.amountVariable`),
    monto_maximo: t(`${prefix}.benefit.amountMax`),
  };
  const yesNoLabels: Record<string, string> = {
    yes: t(`${prefix}.benefit.requiresDocumentValidationYes`),
    no: t(`${prefix}.benefit.requiresDocumentValidationNo`),
  };
  const capTypeLabels: Record<string, string> = {
    none: t(`${prefix}.benefit.capTypeNone`),
    amount: t(`${prefix}.benefit.capTypeAmount`),
    quota: t(`${prefix}.benefit.capTypeQuota`),
  };
  const earlyPaymentScopeLabels: Record<string, string> = {
    cash: t(`${prefix}.benefit.earlyPaymentScopeCash`),
    instalments: t(`${prefix}.benefit.earlyPaymentScopeInstalments`),
    both: t(`${prefix}.benefit.earlyPaymentScopeBoth`),
  };
  const toOption = (
    value: string | null | undefined,
    labels: Record<string, string>,
  ): SelectOptionType | null =>
    value && labels[value] ? { value, label: labels[value] } : null;

  const [formOptions, setFormOptions] = useState<DiscountFormOptions | null>(
    null,
  );
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [
    discountData,
    setDiscountData,
  ] = useState<DiscountWithRelations | null>(null);
  // En edición el detalle llega por separado: hasta que no está, el formulario
  // se mostraría vacío y luego "saltaría" a los datos reales (ver loader abajo).
  const [loadingDiscount, setLoadingDiscount] = useState(!!recordToEdit);
  const isResettingRef = useRef(false);

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setError,
    reset,
    setValue,
  } = methods;

  const continuidadPeriodIds = useWatch({
    control,
    name: 'continuidadPeriodIds',
  });

  // Load form options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      const result = await getDiscountFormOptions();
      if (result.data) {
        setFormOptions(result.data);
      }
      setLoadingOptions(false);
    };
    fetchOptions();
  }, []);

  // Load discount data when editing
  useEffect(() => {
    const fetchDiscountData = async () => {
      if (recordToEdit?.id) {
        const result = await getDiscountById(recordToEdit.id);
        if (result.data) {
          setDiscountData(result.data);
        }
        // Aunque falle, se libera el loader para no dejar la vista colgada.
        setLoadingDiscount(false);
      }
    };
    if (editing) {
      fetchDiscountData();
    }
  }, [recordToEdit?.id, editing]);

  // Update form when discount data and options are loaded
  useEffect(() => {
    if (!formOptions) {
      return;
    }

    if (discountData && editing) {
      const mapToOptions = (
        ids: number[] | undefined,
        options: { id: number; name: string }[],
      ): SelectOptionType[] => {
        if (!ids) {
          return [];
        }
        return ids
          .map((id) => {
            const opt = options.find((o) => o.id === id);
            return opt
              ? ({ value: id, label: opt.name } as SelectOptionType)
              : null;
          })
          .filter((o): o is SelectOptionType => o !== null);
      };

      const inicioAcademicLevelIds = mapToOptions(
        discountData.inicioAcademicLevels?.map((r) => r.academicLevelId),
        formOptions.academicLevels,
      );
      const inicioPeriodIds = mapToOptions(
        discountData.inicioPeriods?.map((r) => r.periodId),
        formOptions.periods,
      );
      const inicioStudyPlanIds = mapToOptions(
        discountData.inicioStudyPlans?.map((r) => r.studyPlanId),
        formOptions.studyPlans,
      );
      const inicioCampusIds = mapToOptions(
        discountData.inicioCampuses?.map((r) => r.campusId),
        formOptions.campuses,
      );
      const inicioScheduleIds = mapToOptions(
        discountData.inicioSchedules?.map((r) => r.scheduleId),
        formOptions.schedules,
      );
      const inicioModalityIds = mapToOptions(
        discountData.inicioModalities?.map((r) => r.modalityId),
        formOptions.modalities,
      );

      const continuidadAcademicLevelIds = mapToOptions(
        discountData.continuidadAcademicLevels?.map((r) => r.academicLevelId),
        formOptions.academicLevels,
      );
      const continuidadStudyPlanIds = mapToOptions(
        discountData.continuidadStudyPlans?.map((r) => r.studyPlanId),
        formOptions.studyPlans,
      );
      const continuidadCampusIds = mapToOptions(
        discountData.continuidadCampuses?.map((r) => r.campusId),
        formOptions.campuses,
      );
      const continuidadScheduleIds = mapToOptions(
        discountData.continuidadSchedules?.map((r) => r.scheduleId),
        formOptions.schedules,
      );
      const continuidadModalityIds = mapToOptions(
        discountData.continuidadModalities?.map((r) => r.modalityId),
        formOptions.modalities,
      );
      const continuidadPeriodIds = mapToOptions(
        discountData.continuidadPeriods?.map((r) => r.periodId),
        formOptions.periods,
      );

      const enrollmentDates =
        discountData.continuidadPeriods?.map((p) => ({
          periodId: p.periodId,
          startDate: p.enrollmentStartDate || '',
          endDate: p.enrollmentEndDate || '',
        })) || [];

      isResettingRef.current = true;
      const applyToValue =
        discountData.appliesToArancel && discountData.appliesToMatricula
          ? 'ambos'
          : discountData.appliesToArancel
          ? 'arancel'
          : discountData.appliesToMatricula
          ? 'matricula'
          : null;
      const isAmountType = AMOUNT_TYPES.includes(
        discountData.applicationType ?? '',
      );
      const isPercentageType = PERCENTAGE_TYPES.includes(
        discountData.applicationType ?? '',
      );
      const applicationValue = isAmountType
        ? discountData.amount != null
          ? String(Math.round(Number(discountData.amount)))
          : ''
        : isPercentageType && discountData.percentage != null
        ? String(Math.round(Number(discountData.percentage)))
        : '';

      reset({
        code: discountData.code,
        name: discountData.name,
        description: discountData.description ?? '',
        benefitType: discountData.benefitType
          ? {
              value: discountData.benefitType.id,
              label: discountData.benefitType.name,
            }
          : null,
        applyTo: toOption(applyToValue, applyToLabels),
        applicationType: toOption(
          discountData.applicationType,
          applicationTypeLabels,
        ),
        applicationValue,
        canCombineWithOtherBenefits:
          discountData.canCombineWithOtherBenefits ?? false,
        requiresDocumentValidation: toOption(
          discountData.requiresDocumentValidation ? 'yes' : 'no',
          yesNoLabels,
        ),
        applicableByEnroller: discountData.applicableByEnroller ?? false,
        capType: toOption(discountData.capType ?? 'none', capTypeLabels),
        maxAmount: TariffFormatter(discountData.maxAmount ?? undefined),
        maxQuota:
          discountData.maxQuota != null
            ? new Intl.NumberFormat('es-CL').format(discountData.maxQuota)
            : '',
        hasAutomaticAssignment: discountData.hasAutomaticAssignment ?? false,
        applyToAllNewAdmissionProcesses:
          discountData.applyToAllNewAdmissionProcesses ?? false,
        minimumGradeAverage: discountData.minimumGradeAverage
          ? String(discountData.minimumGradeAverage)
          : '',
        inicioAcademicLevelIds,
        inicioPeriodIds,
        inicioStudyPlanIds,
        inicioCampusIds,
        inicioScheduleIds,
        inicioModalityIds,
        continuidadAcademicLevelIds,
        continuidadStudyPlanIds,
        continuidadCampusIds,
        continuidadScheduleIds,
        continuidadModalityIds,
        continuidadPeriodIds,
        enrollmentDates,
        earlyPaymentScope: toOption(
          discountData.earlyPaymentScope ?? null,
          earlyPaymentScopeLabels,
        ),
        earlyPaymentCashFrom: discountData.earlyPaymentCashFrom ?? '',
        earlyPaymentCashTo: discountData.earlyPaymentCashTo ?? '',
        earlyPaymentInstalmentsFrom:
          discountData.earlyPaymentInstalmentsFrom ?? '',
        earlyPaymentInstalmentsTo: discountData.earlyPaymentInstalmentsTo ?? '',
      });
    } else if (!editing) {
      isResettingRef.current = true;
      reset(DEFAULT_VALUES);
    }
  }, [formOptions, discountData, editing, reset]);

  // Update enrollment dates when continuidadPeriodIds changes (user interaction only)
  useEffect(() => {
    if (!continuidadPeriodIds || !formOptions) {
      return;
    }

    // Skip when form is being reset with server data
    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }

    const currentDates = methods.getValues('enrollmentDates') || [];
    const selectedPeriodIds = continuidadPeriodIds.map(
      (p: SelectOptionType) => p.value as number,
    );

    const newDates = selectedPeriodIds.map((periodId: number) => {
      const existing = currentDates.find((d) => d.periodId === periodId);
      return existing || { periodId, startDate: '', endDate: '' };
    });

    setValue('enrollmentDates', newDates);
  }, [continuidadPeriodIds, formOptions, setValue, methods]);

  const selectOptions = useMemo(() => {
    if (!formOptions) {
      return null;
    }

    return {
      academicLevels: formOptions.academicLevels.map((al) => ({
        value: al.id,
        label: al.name,
      })),
      periods: formOptions.periods.map((p) => ({
        value: p.id,
        label: p.name,
      })),
      studyPlans: formOptions.studyPlans.map((sp) => ({
        value: sp.id,
        label: sp.name,
      })),
      campuses: formOptions.campuses.map((c) => ({
        value: c.id,
        label: c.name,
      })),
      schedules: formOptions.schedules.map((s) => ({
        value: s.id,
        label: s.name,
      })),
      modalities: formOptions.modalities.map((m) => ({
        value: m.id,
        label: m.name,
      })),
    };
  }, [formOptions]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const extractIds = (options: SelectOptionType[]): number[] =>
        options?.map((o) => o.value as number) || [];

      // "Aplicar a" (select) -> los 2 booleans del modelo.
      const applyTo = values.applyTo?.value;
      const appliesToArancel = applyTo === 'arancel' || applyTo === 'ambos';
      const appliesToMatricula = applyTo === 'matricula' || applyTo === 'ambos';

      // Valor -> percentage o amount según el tipo de aplicación (HU 16116 v2).
      const applicationType = values.applicationType?.value as
        | string
        | undefined;
      const numericValue = values.applicationValue
        ? Number(values.applicationValue)
        : undefined;
      const isAmountType = AMOUNT_TYPES.includes(applicationType ?? '');
      const isPercentageType = PERCENTAGE_TYPES.includes(applicationType ?? '');

      const continuidadPeriods: PeriodWithDates[] =
        values.continuidadPeriodIds?.map((period: SelectOptionType) => {
          const periodId = period.value as number;
          const enrollmentDate = values.enrollmentDates?.find(
            (ed) => ed.periodId === periodId,
          );
          return {
            periodId,
            enrollmentStartDate: enrollmentDate?.startDate || null,
            enrollmentEndDate: enrollmentDate?.endDate || null,
          };
        }) || [];

      // Las condiciones de pronto pago solo viajan cuando el bloque estuvo
      // visible; en cualquier otro caso se limpian, para que un cambio de tipo
      // de beneficio no deje condiciones colgadas.
      const earlyPaymentScope =
        (values.earlyPaymentScope?.value as
          | 'cash'
          | 'instalments'
          | 'both'
          | undefined) ?? null;
      const emptyToNull = (value: string) => (value ? value : null);

      // Tope: sin tope los tres campos viajan en null, así que cambiar un
      // beneficio a "ninguno" limpia de verdad el presupuesto anterior. Los
      // cupos sólo viajan cuando el tope es por cupos.
      const capTypeValue = values.capType?.value as string | undefined;
      const capType =
        capTypeValue === 'amount' || capTypeValue === 'quota'
          ? capTypeValue
          : null;
      const maxAmount =
        capType === 'amount' && values.maxAmount
          ? checkCurrencyAsNumber(values.maxAmount)
          : null;
      const maxQuota =
        capType === 'quota' && values.maxQuota
          ? Number(values.maxQuota.replace(/\D/g, ''))
          : null;

      const requestBody: Discount = {
        code: values.code,
        name: values.name,
        description: values.description,
        hasAutomaticAssignment: values.hasAutomaticAssignment,
        applyToAllNewAdmissionProcesses: values.applyToAllNewAdmissionProcesses,
        minimumGradeAverage: values.minimumGradeAverage
          ? Number(values.minimumGradeAverage)
          : null,
        benefitTypeId: values.benefitType?.value as number,
        appliesToArancel,
        appliesToMatricula,
        applicationType,
        percentage: isPercentageType ? numericValue : undefined,
        amount: isAmountType ? numericValue : undefined,
        // Checkbox "¿Se puede complementar con otro beneficio?" removido de la UI;
        // por ahora se envía siempre true por defecto.
        canCombineWithOtherBenefits: true,
        requiresDocumentValidation:
          values.requiresDocumentValidation?.value === 'yes',
        applicableByEnroller: values.applicableByEnroller,
        capType,
        maxAmount,
        maxQuota,
        id: recordToEdit?.id,
        inicioAcademicLevelIds: extractIds(values.inicioAcademicLevelIds),
        inicioPeriodIds: extractIds(values.inicioPeriodIds),
        inicioStudyPlanIds: extractIds(values.inicioStudyPlanIds),
        inicioCampusIds: extractIds(values.inicioCampusIds),
        inicioScheduleIds: extractIds(values.inicioScheduleIds),
        inicioModalityIds: extractIds(values.inicioModalityIds),
        continuidadAcademicLevelIds: extractIds(
          values.continuidadAcademicLevelIds,
        ),
        continuidadStudyPlanIds: extractIds(values.continuidadStudyPlanIds),
        continuidadCampusIds: extractIds(values.continuidadCampusIds),
        continuidadScheduleIds: extractIds(values.continuidadScheduleIds),
        continuidadModalityIds: extractIds(values.continuidadModalityIds),
        continuidadPeriods,
        earlyPaymentScope,
        earlyPaymentCashFrom: emptyToNull(values.earlyPaymentCashFrom),
        earlyPaymentCashTo: emptyToNull(values.earlyPaymentCashTo),
        earlyPaymentInstalmentsFrom: emptyToNull(
          values.earlyPaymentInstalmentsFrom,
        ),
        earlyPaymentInstalmentsTo: emptyToNull(
          values.earlyPaymentInstalmentsTo,
        ),
      };

      const result = await saveDiscountRequest(requestBody);
      if (result.error && result.error.code === 'HTTP_ERROR') {
        let messageError = t('common.errors.codeError', { code: values.code });
        if (
          result.error.data.message[0] ===
          'code must contain only letters, numbers and underscores'
        ) {
          messageError = t('common.errors.onlyLettersNumbersUnderscore');
        }
        setError('code', {
          message: messageError,
        });
        return;
      }
      onSubmitProp(result);
    },
    [onSubmitProp, setError, t, recordToEdit],
  );

  const getPeriodName = (periodId: number): string => {
    const period = formOptions?.periods.find((p) => p.id === periodId);
    return period?.name || '';
  };

  // En edición se espera también al detalle: así el formulario aparece ya
  // rellenado en vez de mostrarse vacío y completarse después con el reset().
  if (loadingOptions || loadingDiscount) {
    return (
      <div className="p-4 text-center">
        <Loading insideCard />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)} className="p-4">
        {/* Header with back button */}
        <Row className="mb-4">
          <Col>
            <Button
              type="button"
              text={t(`${prefix}.backToList`)}
              outlined
              icon="back"
              onClick={onCancel}
              size="sm"
              className="rounded-pill"
            />
          </Col>
        </Row>

        {/* Description text */}
        <Row className="mb-4">
          <Col xs="12">
            <p className="text-muted mb-0">{t(`${prefix}.formDescription`)}</p>
          </Col>
        </Row>

        <DiscountDataSection
          editing={editing}
          benefitTypes={formOptions?.benefitTypes ?? []}
        />

        {selectOptions && (
          <>
            <DiscountInicioSection selectOptions={selectOptions} />
            <DiscountContinuidadSection
              selectOptions={selectOptions}
              getPeriodName={getPeriodName}
            />
          </>
        )}

        {/* Action buttons */}
        <Row className="mt-5">
          <Col xs="12" className="d-flex justify-content-end">
            <Button
              type="button"
              text={t('common.actions.cancel')}
              outlined
              onClick={onCancel}
              className="mr-3"
            />
            <Button
              type="submit"
              text={t('common.actions.saveChanges')}
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};
